import { cn } from '@invent/wl-ui-kit-next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { readViewerChoice, writeViewerChoice } from '../viewer-store';
import { useViewer } from '../zone';

/**
 * Saving the Lists way (SR-412), one kit for every screen that keeps a thing by a button — a view on
 * Lists, a report, an email template's draft and the email design. **Unsaved is said on the button**
 * (`SaveMenuButton`): a tinted edge, a dot and words on hover, never a line of its own. **Autosave is
 * the reader's setting** (`useReaderSetting`, `AutosaveMenuItem`), last in the menu, on until they
 * turn it off, for what the reader alone changes. **Leaving asks** (`DiscardChangesDialog`), and a
 * revision conflict is one sentence (`conflictWords`).
 */

/** A thing changed by someone else meanwhile — the 409 `revision-conflict` in words. */
export function conflictWords(noun: string): string {
  return `This ${noun} was changed by someone else since you opened it. Reload it — your changes stay here, unsaved.`;
}

/**
 * A reader's on/off setting, kept on this browser for the viewer (`viewer-store.ts`) under
 * `<key>.<viewer>` — `fallback` until they choose. A refused write keeps the choice for the page load.
 */
export function useReaderSetting(key: string, fallback: boolean): [boolean, (on: boolean) => void] {
  const stored = `${key}.${useViewer()}`;
  const [chosen, setChosen] = React.useState<Nullable<{ key: string; on: boolean }>>(null);
  const kept = readViewerChoice(stored).on;
  const on = chosen?.key === stored ? chosen.on : typeof kept === 'boolean' ? kept : fallback;
  const choose = React.useCallback(
    (next: boolean) => {
      setChosen({ key: stored, on: next });
      writeViewerChoice(stored, { on: next });
    },
    [stored]
  );
  return [on, choose];
}

/**
 * The `Save ▾` button and its menu. `unsaved` draws the deep-magenta edge and dot, `unsavedWords` on
 * hover; `busy` says `busyLabel` and holds the menu shut. `footer` is drawn last, after a separator —
 * the reader's `AutosaveMenuItem`.
 */
export function SaveMenuButton({
  label,
  unsaved = false,
  unsavedWords = 'Changed — not saved',
  busy = false,
  busyLabel = 'Saving…',
  align = 'start',
  menuClassName = 'w-80',
  menuDescribedBy,
  children,
  footer
}: {
  label: React.ReactNode;
  unsaved?: boolean;
  unsavedWords?: string;
  busy?: boolean;
  busyLabel?: string;
  /** The menu's edge against the button's — `end` for a button at the right of a header. */
  align?: 'start' | 'end';
  menuClassName?: string;
  menuDescribedBy?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'h-8 gap-1 px-2.5 font-normal',
            unsaved &&
              'border-[var(--color-deep-magenta)] text-[var(--color-deep-magenta)] shadow-[inset_0_0_0_1px_var(--color-deep-magenta)] hover:text-[var(--color-deep-magenta)]'
          )}
          disabled={busy}
          data-save-menu=""
          data-unsaved={unsaved ? '' : undefined}
          title={unsaved ? unsavedWords : undefined}
          aria-description={unsaved ? unsavedWords : undefined}
        >
          {unsaved && <span aria-hidden className="size-2 rounded-full bg-[var(--color-deep-magenta)]" />}
          {busy ? busyLabel : label}
          <ChevronDown className="size-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className={menuClassName} aria-describedby={menuDescribedBy}>
        {children}
        {footer && (
          <>
            <DropdownMenuSeparator />
            {footer}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The reader's *Autosave …* setting as the menu's last item — the menu stays open on a press. */
export function AutosaveMenuItem({
  checked,
  onCheckedChange,
  label,
  hint
}: {
  checked: boolean;
  onCheckedChange: (on: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <DropdownMenuCheckboxItem
      checked={checked}
      onCheckedChange={(next) => onCheckedChange(next === true)}
      onSelect={(event) => event.preventDefault()}
    >
      <span className="grid">
        {label}
        <span className="text-xs text-muted-foreground">{hint}</span>
      </span>
    </DropdownMenuCheckboxItem>
  );
}

/** "Discard 3 unsaved changes?" — or "Discard unsaved changes?" without a count — with *Discard*,
 *  which goes on, and *Cancel*, which stays. `onClosed` runs once the dialog has gone — where going
 *  on unmounts the screen that holds it, it goes from there. */
export function DiscardChangesDialog({
  open,
  count,
  title,
  action = 'Discard',
  description,
  onDiscard,
  onCancel,
  onClosed
}: {
  open: boolean;
  count?: number;
  /** Instead of "Discard N unsaved changes?". */
  title?: string;
  /** The button that goes on — *Discard* unless said. */
  action?: string;
  description: React.ReactNode;
  onDiscard: () => void;
  onCancel: () => void;
  onClosed?: () => void;
}) {
  const changes =
    count === undefined
      ? 'unsaved changes'
      : `${count.toLocaleString('en-US')} unsaved ${count === 1 ? 'change' : 'changes'}`;
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <AlertDialogContent onCloseAutoFocus={onClosed}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title ?? `Discard ${changes}?`}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onDiscard}>{action}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
